import type { MouseEvent } from "react";
import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
  Row,
} from "@hightouch/htui";
import type {
  CarouselMedia,
  EntityCardAction,
} from "src/pages/ad-studio/insights/performance/view/types";

type Tone = "on-media" | "on-surface";

const DOTS: Record<Tone, { current: string; other: string }> = {
  "on-media": { current: "white", other: "whiteAlpha.60" },
  "on-surface": { current: "text.secondary", other: "base.border" },
};

type CreativeCarouselControlsProps = {
  carousel: CarouselMedia;
  tone: Tone;
  performAction: (action: EntityCardAction) => void;
};

export const CreativeCarouselControls = ({
  carousel: { index, count, steps },
  tone,
  performAction,
}: CreativeCarouselControlsProps) => (
  <>
    <CarouselArrow
      side="left"
      tone={tone}
      onStep={() =>
        performAction({
          kind: "EntityCardAction/stepped-media",
          choice: steps[-1],
        })
      }
    />
    <CarouselArrow
      side="right"
      tone={tone}
      onStep={() =>
        performAction({
          kind: "EntityCardAction/stepped-media",
          choice: steps[1],
        })
      }
    />
    <Row
      position="absolute"
      bottom={2}
      left={0}
      right={0}
      justify="center"
      gap={1}
      // The dots are indicators, and their row spans the whole tile.
      // Hit-testing it would end a video's hover scrub mid-sweep.
      pointerEvents="none"
    >
      {Array.from({ length: count }, (_, position) => (
        <Box
          // biome-ignore lint/suspicious/noArrayIndexKey: dots are positional and the list never reorders
          key={position}
          boxSize={1.5}
          borderRadius="full"
          bg={position === index ? DOTS[tone].current : DOTS[tone].other}
        />
      ))}
    </Row>
  </>
);

const CarouselArrow = ({
  side,
  tone,
  onStep,
}: {
  side: "left" | "right";
  tone: Tone;
  onStep: () => void;
}) => (
  <Box
    position="absolute"
    top="50%"
    left={side === "left" ? 1 : undefined}
    right={side === "right" ? 1 : undefined}
    transform="translateY(-50%)"
  >
    <ArrowButton side={side} tone={tone} onStep={onStep} />
  </Box>
);

const ArrowButton = ({
  side,
  tone,
  onStep,
}: {
  side: "left" | "right";
  tone: Tone;
  onStep: () => void;
}) => {
  const label = side === "left" ? "Previous creative" : "Next creative";
  const icon = side === "left" ? ChevronLeftIcon : ChevronRightIcon;
  const onClick = (event: MouseEvent) => {
    event.stopPropagation();
    onStep();
  };
  switch (tone) {
    case "on-media": {
      return (
        <IconButton
          aria-label={label}
          icon={icon}
          size="sm"
          shape="circle"
          bg="blackAlpha.60"
          color="white"
          _hover={{ bg: "blackAlpha.60" }}
          onClick={onClick}
        />
      );
    }
    case "on-surface": {
      return (
        <IconButton
          aria-label={label}
          icon={icon}
          variant="secondary"
          size="sm"
          shape="circle"
          onClick={onClick}
        />
      );
    }
    default: {
      return tone satisfies never;
    }
  }
};
